// Tier helpers — Free / Pro subscription gating for DropPin.
//
// v0.38.0 — initial. Until InSite-ID cutover lands (DropPin v0.39.0+
//           plan), tenant tier reads from the current user payload in
//           localStorage, specifically `user.tenants[0].products` array
//           where InSite-ID stores per-product entitlements:
//
//             products: [
//               { code: 'droppin',  tier: 'pro',  status: 'active', ... },
//               { code: 'inspecpdf', tier: 'free', status: 'active', ... }
//             ]
//
// For users created on DropPin's local TenantUsers table (pre-cutover),
// there are no `products` entries — they default to Free. Once InSite-ID
// is the source of truth, every tenant gets a TenantProducts row at
// signup and this function picks the right one up automatically.
//
// API:
//   getTenantTier()       — returns 'free' | 'pro'
//   isFreeTier()          — boolean shortcut
//   isProTier()           — boolean shortcut
//   FREE_LIMITS           — { activeProjects, defectsPerProject, ... }
//   limitMessage(kind)    — human-readable string for the upgrade prompt

import { getCurrentUser } from './user';

export const TIER_FREE = 'free';
export const TIER_PRO  = 'pro';

// Free tier limits. When InSite-ID + Stripe land these become the
// `TenantProducts.tier === 'free'` enforced caps.
export const FREE_LIMITS = {
  activeProjects:      1,
  defectsPerProject:   50,
  drawingsPerReport:   1,
  reportsPerMonth:     5,
  photoStorageMB:      200,
};

const PRODUCT_CODE = 'droppin';

export function getTenantTier() {
  const u = getCurrentUser();
  if (!u) return TIER_FREE;
  // InSite-ID-shaped payload: user.tenants[0].products[].
  const tenants = Array.isArray(u.tenants) ? u.tenants : [];
  for (const t of tenants) {
    const products = Array.isArray(t.products) ? t.products : [];
    const droppin = products.find(p => p && p.code === PRODUCT_CODE);
    if (droppin && (droppin.tier === TIER_PRO || droppin.tier === 'team' || droppin.tier === 'enterprise')) {
      return TIER_PRO;
    }
  }
  // Legacy local-auth payload (DropPin v0.31.x). No products array;
  // safe default is Free.
  return TIER_FREE;
}

export function isFreeTier() { return getTenantTier() === TIER_FREE; }
export function isProTier()  { return getTenantTier() === TIER_PRO;  }

// Human-readable upgrade-prompt copy keyed by which limit was hit.
export function limitMessage(kind) {
  switch (kind) {
    case 'activeProjects':
      return `Free tier supports ${FREE_LIMITS.activeProjects} active project. Upgrade to Pro for unlimited projects.`;
    case 'defectsPerProject':
      return `Free tier supports ${FREE_LIMITS.defectsPerProject} defects per project. Upgrade to Pro for unlimited.`;
    case 'drawingsPerReport':
      return `Free tier supports ${FREE_LIMITS.drawingsPerReport} drawing per report. Upgrade to Pro for unlimited.`;
    case 'reportsPerMonth':
      return `Free tier allows ${FREE_LIMITS.reportsPerMonth} reports per month. Upgrade to Pro for unlimited.`;
    case 'shareLinks':
      return `Public share links are a Pro feature. Upgrade to share reports outside your team.`;
    case 'watermark':
      return `White-label PDF reports (no DropPin watermark) are a Pro feature.`;
    case 'contactInvite':
      return `Inviting contacts to log in (so subbies can see only their assigned items) is a Pro feature.`;
    case 'whitelabel':
      return `White-labelled PDF reports (your logo, your brand colour, no DropPin watermark) ship with Pro. Your settings save now — they switch on the moment you upgrade.`;
    default:
      return `This feature is part of DropPin Pro.`;
  }
}
